import { create } from 'zustand';

export type AppStatus =
  | 'boot'
  | 'checking'
  | 'ready'
  | 'unsupported'
  | 'error';

export interface BrowserCompat {
  webAudio: boolean;
  mediaDevices: boolean;
  serviceWorker: boolean;
  wasm: boolean;
}

interface AppState {
  status: AppStatus;
  compat: BrowserCompat;
  errorMessage: string | null;

  setStatus: (s: AppStatus) => void;
  setError: (msg: string | null) => void;
  checkCompatibility: () => BrowserCompat;
}

const detectCompat = (): BrowserCompat => ({
  webAudio:
    typeof window !== 'undefined' &&
    ('AudioContext' in window ||
      'webkitAudioContext' in (window as unknown as Record<string, unknown>)),
  mediaDevices:
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia,
  serviceWorker:
    typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
  wasm: typeof WebAssembly !== 'undefined',
});

export const useAppStore = create<AppState>((set) => ({
  status: 'boot',
  compat: {
    webAudio: false,
    mediaDevices: false,
    serviceWorker: false,
    wasm: false,
  },
  errorMessage: null,

  setStatus: (status) => set({ status }),
  setError: (errorMessage) =>
    set({ errorMessage, status: errorMessage ? 'error' : 'ready' }),

  checkCompatibility: () => {
    const compat = detectCompat();
    const supported = compat.webAudio && compat.mediaDevices && compat.wasm;
    set({
      compat,
      status: supported ? 'ready' : 'unsupported',
      errorMessage: supported
        ? null
        : 'Browser missing Web Audio, camera, or WebAssembly support. Use Chrome, Edge, or Safari ≥ 17.',
    });
    return compat;
  },
}));
